import { useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useAudiences } from 'app/audiences/application'
import { ConnectionIcon } from 'app/common/presentation'
import { formatCurrency } from 'app/common/presentation/format'
import { AudienceDayRevenue, AudiencesDayRevenue } from 'app/dashbaord/types'

import { Table } from 'components'

import Tile, { TileContentProps } from './Tile'
import styles from './Tile.module.scss'

const determineTopRevenueAudiences = (revenues: AudiencesDayRevenue[]) =>
  revenues
    .reduce((acc, audiencesDayRevenue) => {
      audiencesDayRevenue.audiences.forEach((audienceDayRevenue) => {
        const audienceTotalRevenue = acc.find(
          (audienceRevenue) => audienceRevenue.id === audienceDayRevenue.id,
        )
        if (audienceTotalRevenue) {
          audienceTotalRevenue.revenue += audienceDayRevenue.revenue
        } else {
          acc.push(audienceDayRevenue)
        }
      })

      return acc
    }, [] as AudienceDayRevenue[])
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)

interface TopAudiencesProps extends TileContentProps {
  revenues: AudiencesDayRevenue[]
}

const TopAudiences = ({ days, revenues, translate }: TopAudiencesProps) => {
  const { audiences } = useAudiences()
  const navigate = useNavigate()

  const topRevenueAudiences = useMemo(() => determineTopRevenueAudiences(revenues), [revenues])

  const renderConnection = (audienceId: string) => {
    const conn = audiences.find((audience) => audience.id === audienceId)?.connection

    if (conn) {
      return <ConnectionIcon connection={conn} />
    }

    return '-'
  }

  return (
    <Tile className={styles.topAudiences} days={days} tileKey="topAudiences" translate={translate}>
      <Table.Table
        className={styles.table}
        columns={[
          translate('topAudiences.columns.name'),
          translate('topAudiences.columns.connection'),
          translate('topAudiences.columns.revenue'),
        ]}
        data={topRevenueAudiences}
        renderRow={(topRevenueAudience, index) => (
          <Table.Row key={index}>
            <td
              title={topRevenueAudience.name}
              onClick={() => void navigate(`/audiences/${topRevenueAudience.id}`)}
            >
              {topRevenueAudience.name}
            </td>
            <td>{renderConnection(topRevenueAudience.id)}</td>
            <Table.Cell>{formatCurrency(topRevenueAudience.revenue)}</Table.Cell>
          </Table.Row>
        )}
      />
    </Tile>
  )
}

export default TopAudiences
